import { useState } from "react";
import { Plus, Search, Filter, AlertCircle } from "lucide-react";
import {
  useVehicleMaintenanceByVehicle,
  useDeleteMaintenanceRecord,
} from "@/hooks/useVehicleMaintenance";
import { VehicleMaintenanceList } from "./VehicleMaintenanceList";
import { VehicleMaintenanceModal } from "./VehicleMaintenanceModal";
import { useTranslation } from "react-i18next";
import type { Vehicle, VehicleMaintenance } from "@/types";

interface VehicleMaintenanceSectionProps {
  vehicle: Vehicle;
  className?: string;
}

export function VehicleMaintenanceSection({
  vehicle,
  className = "",
}: VehicleMaintenanceSectionProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState<
    VehicleMaintenance | undefined
  >();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("");

  const {
    data: maintenanceRecords = [],
    isLoading: maintenanceLoading,
    error: maintenanceError,
  } = useVehicleMaintenanceByVehicle(vehicle.id);

  const deleteMaintenance = useDeleteMaintenanceRecord();

  const handleCreateMaintenance = () => {
    setSelectedMaintenance(undefined);
    setIsMaintenanceModalOpen(true);
  };

  const handleEditMaintenance = (maintenance: VehicleMaintenance) => {
    setSelectedMaintenance(maintenance);
    setIsMaintenanceModalOpen(true);
  };

  const handleDeleteMaintenance = async (maintenanceId: string) => {
    if (deleteConfirm === maintenanceId) {
      try {
        await deleteMaintenance.mutateAsync({
          id: maintenanceId,
          vehicleId: vehicle.id,
        });
        setDeleteConfirm(null);
      } catch (error) {
        console.error("Failed to delete maintenance record:", error);
      }
    } else {
      setDeleteConfirm(maintenanceId);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const handleCloseMaintenanceModal = () => {
    setIsMaintenanceModalOpen(false);
    setSelectedMaintenance(undefined);
  };

  // Filter maintenance records based on search and type filter
  const filteredRecords = maintenanceRecords.filter((record) => {
    const matchesSearch =
      !searchTerm ||
      record.workType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.maintainers?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = !filterType || record.type === filterType;

    return matchesSearch && matchesType;
  });

  return (
    <div className={`bg-white rounded-lg border border-secondary-200 ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">
              {t("pages:vehicles.maintenance.sectionTitle")}
            </h2>
            <p className="text-sm text-secondary-600 mt-1">
              {t("pages:vehicles.maintenance.sectionSubtitle", {
                vehicle:
                  vehicle.name ||
                  `${vehicle.year} ${vehicle.maker} ${vehicle.model}`,
              })}
            </p>
          </div>
          <button
            onClick={handleCreateMaintenance}
            className="btn-primary px-4 py-2 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>{t("pages:vehicles.maintenance.addMaintenance")}</span>
          </button>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-4 w-4" />
            <input
              type="text"
              placeholder={t("pages:vehicles.maintenance.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-4 w-4" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input-field pl-10 pr-10 min-w-[180px]"
            >
              <option value="">{t("pages:vehicles.maintenance.filterAllTypes")}</option>
              <option value="scheduled">{t("pages:vehicles.maintenanceTypes.scheduled")}</option>
              <option value="unexpected">{t("pages:vehicles.maintenanceTypes.unexpected")}</option>
              <option value="preventative">{t("pages:vehicles.maintenanceTypes.preventative")}</option>
              <option value="other">{t("pages:vehicles.maintenanceTypes.other")}</option>
            </select>
          </div>
        </div>

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-yellow-400 mr-2" />
                <p className="text-yellow-800">
                  {t("pages:vehicles.maintenance.deleteConfirmMessage")}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDeleteMaintenance(deleteConfirm)}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                  disabled={deleteMaintenance.isPending}
                >
                  {deleteMaintenance.isPending ? t("pages:sessions.deleting") : t("common:delete")}
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="bg-secondary-300 text-secondary-700 px-3 py-1 rounded text-sm hover:bg-secondary-400"
                >
                  {t("common:cancel")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Maintenance Records List */}
        {maintenanceError ? (
          <div className="text-center py-8">
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-600">{t("pages:vehicles.maintenance.loadFailed")}</p>
          </div>
        ) : (
          <VehicleMaintenanceList
            maintenanceRecords={filteredRecords}
            isLoading={maintenanceLoading}
            onEdit={handleEditMaintenance}
            onDelete={handleDeleteMaintenance}
            deleteConfirm={deleteConfirm}
          />
        )}
      </div>

      {/* Maintenance Modal */}
      <VehicleMaintenanceModal
        isOpen={isMaintenanceModalOpen}
        onClose={handleCloseMaintenanceModal}
        vehicle={vehicle}
        maintenance={selectedMaintenance}
      />
    </div>
  );
}
